import React, { useState } from 'react';
import { useMaintenance } from '../context/MaintenanceContext';
import { useEntitlements } from '../context/EntitlementsContext';
import { Warning, Info, X, Wrench } from '@phosphor-icons/react';

/** SEVERITY_TONE — palette per severity. Banner is full-bleed and
 *  pinned to the top of the viewport so it never overlaps content. */
const SEVERITY_TONE = {
    info:     { bg: 'bg-[#0F4C5C]', text: 'text-white',  icon: Info },
    warning:  { bg: 'bg-[#B45309]', text: 'text-white',  icon: Warning },
    critical: { bg: 'bg-[#7F1D1D]', text: 'text-white',  icon: Warning },
};

/** Thin live banner pinned to the top of every page. Renders nothing
 *  if mode !== 'banner' (takeover is handled separately by
 *  MaintenanceTakeover). Dismissible per-session so users can hide it
 *  if the message is informational — but it reappears on next visit. */
export default function MaintenanceBanner() {
    const { cfg } = useMaintenance();
    const ent = useEntitlements();
    const [dismissedKey, setDismissedKey] = useState(() => {
        try { return sessionStorage.getItem('defectr:maint:dismissed') || ''; } catch { return ''; }
    });

    if (!cfg || cfg.mode !== 'banner') return null;
    if (cfg.bypass_admins && ent?.isAdmin) {
        // Admins still see the banner so they remember it's live, but with
        // a small "(admin preview)" tag. We do NOT hide it — the admin needs
        // visibility on what users are seeing.
    }

    // Build a stable key so a fresh message text resurfaces the banner
    // for users who previously dismissed.
    const sessionKey = `${cfg.title}|${cfg.message}|${cfg.expires_at || ''}`;
    if (dismissedKey === sessionKey) return null;

    const tone = SEVERITY_TONE[cfg.severity] || SEVERITY_TONE.info;
    const Icon = tone.icon;

    const onDismiss = () => {
        try { sessionStorage.setItem('defectr:maint:dismissed', sessionKey); } catch { /* noop */ }
        setDismissedKey(sessionKey);
    };

    return (
        <div
            data-testid="maintenance-banner"
            data-severity={cfg.severity}
            className={`${tone.bg} ${tone.text} w-full px-4 py-2.5 flex items-start sm:items-center gap-3`}
            role="alert"
        >
            <Icon size={18} weight="fill" className="shrink-0 mt-0.5 sm:mt-0" />
            <div className="flex-1 min-w-0 text-sm leading-tight">
                {cfg.title ? <span className="font-display font-semibold mr-2" data-testid="maintenance-banner-title">{cfg.title}</span> : null}
                <span className="text-white/90" data-testid="maintenance-banner-message">{cfg.message}</span>
                {cfg.bypass_admins && ent?.isAdmin ? (
                    <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.18em] bg-white/20 px-1.5 py-0.5">admin preview</span>
                ) : null}
            </div>
            <button
                type="button"
                onClick={onDismiss}
                data-testid="maintenance-banner-dismiss"
                aria-label="Dismiss banner"
                className="shrink-0 p-1 hover:bg-white/10"
            >
                <X size={16} weight="bold" />
            </button>
        </div>
    );
}

/** Full-page takeover. Replaces the entire app when mode==='takeover' and
 *  the current user is NOT an exempted admin. Admins still see the app
 *  with a sticky "Takeover active — users locked out" warning strip
 *  rendered separately by AdminTakeoverWarning.  */
export function MaintenanceTakeover({ children }) {
    const { cfg, loading } = useMaintenance();
    const ent = useEntitlements();

    // While we haven't read /api/maintenance yet, render the app
    // optimistically. A 1-frame flash beats locking users out by mistake.
    if (loading) return children;
    if (cfg?.mode !== 'takeover') return children;
    if (cfg.bypass_admins && ent?.isAdmin) return children;

    const tone = SEVERITY_TONE[cfg.severity] || SEVERITY_TONE.info;

    return (
        <div data-testid="maintenance-takeover" className="min-h-screen bg-[#09090B] text-white flex items-center justify-center px-6">
            <div className="max-w-lg text-center">
                <div className={`${tone.bg} inline-flex items-center gap-2 px-3 py-1.5 mb-6 font-mono text-[10px] uppercase tracking-[0.2em]`}>
                    <Wrench size={12} weight="bold" /> Maintenance in progress
                </div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                    {cfg.title || "We'll be right back"}
                </h1>
                {cfg.message ? (
                    <p className="mt-4 text-white/80 text-base leading-relaxed whitespace-pre-line" data-testid="maintenance-takeover-message">
                        {cfg.message}
                    </p>
                ) : null}
                {cfg.expires_at ? (
                    <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-white/50" data-testid="maintenance-takeover-expires">
                        Expected back by {formatExpires(cfg.expires_at)}
                    </p>
                ) : null}
                <div className="mt-10 text-xs text-white/40">
                    <a href="mailto:support@defectr.co.uk" className="underline decoration-[#1FA5CF] underline-offset-4">support@defectr.co.uk</a> · DefectR
                </div>
            </div>
        </div>
    );
}

/** Small warning strip shown to admins when takeover is active for users.
 *  Admins see this AT the top of the app so they remember non-admin users
 *  are currently locked out. */
export function AdminTakeoverWarning() {
    const { cfg } = useMaintenance();
    const ent = useEntitlements();
    if (cfg?.mode !== 'takeover') return null;
    if (!ent?.isAdmin) return null;
    return (
        <div data-testid="maintenance-admin-warning" className="w-full bg-[#7F1D1D] text-white px-4 py-2 text-sm flex items-center gap-2">
            <Warning size={16} weight="fill" />
            <span><strong className="font-display font-semibold">Takeover active</strong> — non-admin users are locked out. Go to <a href="/admin" className="underline">/admin</a> to toggle off.</span>
        </div>
    );
}

function formatExpires(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
        return iso;
    }
}
